import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { time, titleCase, today } from '../lib/format';
import { PageHeader } from '../components/Layout';
import { TableSetup } from '../components/TableSetup';
import { TableQrCodes } from '../components/TableQrCodes';
import {
  Empty,
  ErrorState,
  Kpi,
  Loading,
  Modal,
  Select,
  StatusChip,
  TextInput,
  useToast,
} from '../components/ui';

interface Reservation {
  id: string;
  guestName: string;
  phone: string;
  partySize: number;
  startsAt: string;
  endsAt: string;
  status: string;
  tableId: string | null;
  tableCode: string | null;
  note: string | null;
}

interface TableRow {
  id: string;
  code: string;
  seats: number;
  section: string;
  isOccupied: boolean;
  openTicket: { number: string } | null;
}

const NEXT_STATUS: Record<string, string[]> = {
  BOOKED: ['CONFIRMED', 'CANCELLED', 'NO_SHOW'],
  CONFIRMED: ['SEATED', 'CANCELLED', 'NO_SHOW'],
  SEATED: ['COMPLETED'],
};

export function ReservationsPage() {
  const queryClient = useQueryClient();
  const { fail, push } = useToast();
  const { can } = useAuth();
  const [date, setDate] = useState(today());
  const [creating, setCreating] = useState(false);
  const [tableSetup, setTableSetup] = useState(false);
  const [qrCodes, setQrCodes] = useState(false);

  const reservations = useQuery({
    queryKey: ['reservations', date],
    queryFn: () => api.get<Reservation[]>(`/api/reservations?date=${date}`),
  });
  const tables = useQuery({
    queryKey: ['tables'],
    queryFn: () => api.get<TableRow[]>('/api/tables'),
  });

  const setStatus = useMutation({
    mutationFn: (input: { id: string; status: string }) =>
      api.patch(`/api/reservations/${input.id}`, { status: input.status }),
    onSuccess: () => {
      push('Booking updated');
      void queryClient.invalidateQueries({ queryKey: ['reservations'] });
    },
    onError: fail,
  });

  if (reservations.isLoading) return <Loading label="Loading the day sheet…" />;
  if (reservations.error)
    return <ErrorState error={reservations.error} onRetry={() => void reservations.refetch()} />;

  const rows = reservations.data ?? [];
  const covers = rows
    .filter((row) => !['CANCELLED', 'NO_SHOW'].includes(row.status))
    .reduce((sum, row) => sum + row.partySize, 0);

  return (
    <div className="stack">
      <PageHeader
        title="Reservations"
        subtitle="Restaurant table bookings and the floor plan"
        actions={
          <>
            <input
              className="input"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={{ width: 160 }}
            />
            {can('reservation:manage') ? (
              <>
                <button className="btn" onClick={() => setTableSetup(true)}>
                  Set up tables
                </button>
                <button className="btn" onClick={() => setQrCodes(true)}>
                  Table QR codes
                </button>
              </>
            ) : null}
            {can('reservation:manage') ? (
              <button className="btn primary" onClick={() => setCreating(true)}>
                + New booking
              </button>
            ) : null}
          </>
        }
      />

      <div className="kpis">
        <Kpi label="Bookings" value={rows.length} foot={`${covers} covers expected`} />
        <Kpi
          label="Seated"
          value={rows.filter((r) => r.status === 'SEATED').length}
          foot="Currently dining"
          tone="blue"
        />
        <Kpi
          label="No-shows"
          value={rows.filter((r) => r.status === 'NO_SHOW').length}
          foot="Marked on this date"
          tone={rows.some((r) => r.status === 'NO_SHOW') ? 'amber' : ''}
        />
        <Kpi
          label="Tables free"
          value={(tables.data ?? []).filter((t) => !t.isOccupied).length}
          foot={`${tables.data?.length ?? 0} tables in service`}
        />
      </div>

      <div className="grid-32">
        <div className="card">
          <div className="card-head">
            <h2>Day sheet</h2>
          </div>
          {rows.length === 0 ? (
            <Empty icon="◷" message="No bookings for this date" />
          ) : (
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Guest</th>
                    <th className="num">Party</th>
                    <th>Table</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td className="b nowrap">{time(row.startsAt)}</td>
                      <td>
                        <div className="b">{row.guestName}</div>
                        <div className="xs muted">{row.phone}</div>
                      </td>
                      <td className="num">{row.partySize}</td>
                      <td>{row.tableCode ?? <span className="muted">Unassigned</span>}</td>
                      <td>
                        <StatusChip status={row.status} />
                      </td>
                      <td className="right">
                        {can('reservation:manage') ? (
                          <div className="row gap-4" style={{ justifyContent: 'flex-end' }}>
                            {(NEXT_STATUS[row.status] ?? []).map((next) => (
                              <button
                                key={next}
                                className={`btn sm ${next === 'CANCELLED' || next === 'NO_SHOW' ? 'ghost' : ''}`}
                                onClick={() => setStatus.mutate({ id: row.id, status: next })}
                              >
                                {titleCase(next)}
                              </button>
                            ))}
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Floor plan</h2>
            <span className="chip">
              {(tables.data ?? []).filter((t) => t.isOccupied).length} occupied
            </span>
          </div>
          <div className="card-body">
            <div
              className="rack"
              style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(108px, 1fr))' }}
            >
              {(tables.data ?? []).map((table) => (
                <div
                  key={table.id}
                  className={`room ${table.isOccupied ? 'OCCUPIED' : 'VACANT'}`}
                  style={{ cursor: 'default' }}
                >
                  <div className="r-no">{table.code}</div>
                  <div className="r-type">
                    {table.seats} seats · {table.section}
                  </div>
                  <div className="xs muted" style={{ marginTop: 6 }}>
                    {table.openTicket ? table.openTicket.number : 'Free'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {tableSetup ? <TableSetup onClose={() => setTableSetup(false)} /> : null}
      {qrCodes ? <TableQrCodes onClose={() => setQrCodes(false)} /> : null}

      {creating ? (
        <NewReservationModal
          date={date}
          tables={tables.data ?? []}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void queryClient.invalidateQueries({ queryKey: ['reservations'] });
          }}
        />
      ) : null}
    </div>
  );
}

function NewReservationModal({
  date,
  tables,
  onClose,
  onCreated,
}: {
  date: string;
  tables: TableRow[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const { fail, push } = useToast();
  const [form, setForm] = useState({
    guestName: '',
    phone: '',
    email: '',
    partySize: 2,
    date,
    time: '19:30',
    durationMinutes: 90,
    tableId: '',
    note: '',
  });

  const create = useMutation({
    mutationFn: () =>
      api.post('/api/reservations', {
        ...form,
        tableId: form.tableId || undefined,
        email: form.email || undefined,
      }),
    onSuccess: () => {
      push(`Table booked for ${form.guestName}`, 'ok');
      onCreated();
    },
    onError: fail,
  });

  return (
    <Modal
      title="New booking"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={!form.guestName.trim() || !form.phone.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            Book table
          </button>
        </>
      }
    >
      <div className="form-grid">
        <TextInput
          className="span2"
          label="Guest name *"
          autoFocus
          value={form.guestName}
          onChange={(e) => setForm({ ...form, guestName: e.target.value })}
        />
        <TextInput
          label="Phone *"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
        />
        <TextInput
          label="Email"
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        <TextInput
          label="Party size"
          type="number"
          min={1}
          value={form.partySize}
          onChange={(e) => setForm({ ...form, partySize: Number(e.target.value) || 1 })}
        />
        <TextInput
          label="Date"
          type="date"
          value={form.date}
          onChange={(e) => setForm({ ...form, date: e.target.value })}
        />
        <TextInput
          label="Time"
          type="time"
          value={form.time}
          onChange={(e) => setForm({ ...form, time: e.target.value })}
        />
        <Select
          label="Duration"
          value={String(form.durationMinutes)}
          onChange={(e) => setForm({ ...form, durationMinutes: Number(e.target.value) })}
        >
          <option value="60">1 hour</option>
          <option value="90">1.5 hours</option>
          <option value="120">2 hours</option>
          <option value="180">3 hours</option>
        </Select>
        <Select
          className="span2"
          label="Table"
          value={form.tableId}
          onChange={(e) => setForm({ ...form, tableId: e.target.value })}
          hint="Leave unassigned to seat on arrival"
        >
          <option value="">Unassigned</option>
          {tables
            .filter((table) => table.seats >= form.partySize)
            .map((table) => (
              <option key={table.id} value={table.id}>
                {table.code} · {table.seats} seats · {table.section}
              </option>
            ))}
        </Select>
        <TextInput
          className="span2"
          label="Note"
          value={form.note}
          onChange={(e) => setForm({ ...form, note: e.target.value })}
          placeholder="Allergies, occasion, seating preference"
        />
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/* Housekeeping                                                        */
/* ------------------------------------------------------------------ */

interface Task {
  id: string;
  roomId: string;
  roomNumber: string;
  floor: number;
  roomStatus: string;
  type: string;
  status: string;
  assignedTo: string | null;
  note: string | null;
  createdAt: string;
}

interface RoomOption {
  id: string;
  number: string;
  status: string;
}

export function HousekeepingPage() {
  const queryClient = useQueryClient();
  const { fail, push } = useToast();
  const { can } = useAuth();
  const [filter, setFilter] = useState('OPEN');
  const [creating, setCreating] = useState(false);

  const tasks = useQuery({
    queryKey: ['housekeeping', filter],
    queryFn: () =>
      api.get<Task[]>(
        `/api/housekeeping/tasks${filter === 'ALL' || filter === 'OPEN' ? '' : `?status=${filter}`}`,
      ),
    refetchInterval: 30_000,
  });

  const update = useMutation({
    mutationFn: (input: { id: string; status: string }) =>
      api.patch(`/api/housekeeping/tasks/${input.id}`, { status: input.status }),
    onSuccess: () => {
      push('Task updated');
      void queryClient.invalidateQueries({ queryKey: ['housekeeping'] });
      void queryClient.invalidateQueries({ queryKey: ['rooms'] });
    },
    onError: fail,
  });

  if (tasks.isLoading) return <Loading label="Loading tasks…" />;
  if (tasks.error) return <ErrorState error={tasks.error} onRetry={() => void tasks.refetch()} />;

  const all = tasks.data ?? [];
  const rows = filter === 'OPEN' ? all.filter((task) => task.status !== 'DONE') : all;

  return (
    <div className="stack">
      <PageHeader
        title="Housekeeping"
        subtitle="Room cleaning and maintenance tasks"
        actions={
          <>
            <div className="seg">
              {['OPEN', 'PENDING', 'IN_PROGRESS', 'DONE', 'ALL'].map((option) => (
                <button
                  key={option}
                  className={option === filter ? 'active' : ''}
                  onClick={() => setFilter(option)}
                >
                  {titleCase(option)}
                </button>
              ))}
            </div>
            {can('housekeeping:update') ? (
              <button className="btn primary" onClick={() => setCreating(true)}>
                + New task
              </button>
            ) : null}
          </>
        }
      />

      <div className="kpis">
        <Kpi
          label="Pending"
          value={all.filter((t) => t.status === 'PENDING').length}
          foot="Not started"
          tone="amber"
        />
        <Kpi
          label="In progress"
          value={all.filter((t) => t.status === 'IN_PROGRESS').length}
          foot="Being cleaned"
          tone="blue"
        />
        <Kpi
          label="Completed"
          value={all.filter((t) => t.status === 'DONE').length}
          foot="Rooms returned to sale"
        />
        <Kpi
          label="Blocked"
          value={all.filter((t) => t.status === 'BLOCKED').length}
          foot="Needs attention"
          tone={all.some((t) => t.status === 'BLOCKED') ? 'red' : ''}
        />
      </div>

      <div className="card">
        {rows.length === 0 ? (
          <Empty icon="✓" message="Nothing outstanding" />
        ) : (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Room</th>
                  <th>Task</th>
                  <th>Assigned to</th>
                  <th>Room status</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((task) => (
                  <tr key={task.id}>
                    <td>
                      <div className="b">{task.roomNumber}</div>
                      <div className="xs muted">Floor {task.floor}</div>
                    </td>
                    <td>
                      {titleCase(task.type)}
                      {task.note ? <div className="xs muted">{task.note}</div> : null}
                    </td>
                    <td className="dim">{task.assignedTo ?? 'Unassigned'}</td>
                    <td>
                      <StatusChip status={task.roomStatus} />
                    </td>
                    <td>
                      <StatusChip status={task.status} />
                    </td>
                    <td className="right">
                      {can('housekeeping:update') && task.status !== 'DONE' ? (
                        <div className="row gap-4" style={{ justifyContent: 'flex-end' }}>
                          {task.status === 'PENDING' ? (
                            <button
                              className="btn sm"
                              onClick={() => update.mutate({ id: task.id, status: 'IN_PROGRESS' })}
                            >
                              Start
                            </button>
                          ) : null}
                          <button
                            className="btn sm primary"
                            onClick={() => update.mutate({ id: task.id, status: 'DONE' })}
                          >
                            Mark done
                          </button>
                          <button
                            className="btn ghost sm"
                            onClick={() => update.mutate({ id: task.id, status: 'BLOCKED' })}
                          >
                            Block
                          </button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {creating ? (
        <NewTaskModal
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void queryClient.invalidateQueries({ queryKey: ['housekeeping'] });
          }}
        />
      ) : null}
    </div>
  );
}

function NewTaskModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { fail, push } = useToast();
  const [roomId, setRoomId] = useState('');
  const [type, setType] = useState('DEPARTURE_CLEAN');
  const [note, setNote] = useState('');

  const rooms = useQuery({
    queryKey: ['rooms'],
    queryFn: () => api.get<RoomOption[]>('/api/rooms'),
  });

  const create = useMutation({
    mutationFn: () => api.post('/api/housekeeping/tasks', { roomId, type, note }),
    onSuccess: () => {
      push('Task created', 'ok');
      onCreated();
    },
    onError: fail,
  });

  return (
    <Modal
      title="New housekeeping task"
      size="narrow"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={!roomId || create.isPending}
            onClick={() => create.mutate()}
          >
            Create task
          </button>
        </>
      }
    >
      <div className="col gap-16">
        <Select label="Room" value={roomId} onChange={(e) => setRoomId(e.target.value)}>
          <option value="">Select a room…</option>
          {(rooms.data ?? []).map((room) => (
            <option key={room.id} value={room.id}>
              {room.number} · {titleCase(room.status)}
            </option>
          ))}
        </Select>
        <Select label="Task type" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="DEPARTURE_CLEAN">Departure clean</option>
          <option value="STAYOVER_CLEAN">Stayover clean</option>
          <option value="DEEP_CLEAN">Deep clean</option>
          <option value="INSPECTION">Inspection</option>
          <option value="MAINTENANCE">Maintenance</option>
        </Select>
        <TextInput label="Note" value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
    </Modal>
  );
}
